import type { Dayjs } from 'dayjs'

import { appDay } from 'shared/day'

import type { Message, RenderContent } from '../model/types'

import { ROUTES } from '../const'

const NOW = appDay()

const formatChatDate = (day: Dayjs) => {
	if (day.isSame(NOW, 'day')) {
		return 'Today'
	}

	return formatDateTime(day)
}

const formatTime = (timeString: string) => {
	const asDay = appDay(timeString)
	return asDay.format('HH:mm')
}

const formatDateTime = (dateTimeString: Dayjs | string) => {
	const asDay = appDay(dateTimeString)

	if (asDay.isSame(NOW, 'day')) {
		return asDay.format('HH:mm')
	}
	if (asDay.isSame(NOW, 'year')) {
		return asDay.format('DD MMM')
	}
	return asDay.format('DD MMM, YYYY')
}

const findRenderContent = (
	contents: RenderContent[]
): keyof RenderContent | null => {
	for (const content of contents) {
		for (const key in content) {
			const typed = key as keyof RenderContent

			const value = content[typed]

			if (value !== null) {
				return typed
			}
		}
	}

	return null
}

type CreatedAt = Pick<Message, 'created_at'>

const sortOldFirst = (first: CreatedAt, second: CreatedAt) =>
	appDay(first.created_at).diff(second.created_at)

const sortNewFirst = (first: CreatedAt, second: CreatedAt) =>
	appDay(second.created_at).diff(first.created_at)

interface MessageWithFlag {
	id: string
	showAvatar: boolean
}

class MessageTransformer {
	lastDay: Dayjs | null = null

	#lastUser = ''

	transform(message: Message): MessageWithFlag {
		const asDay = appDay(message.created_at)

		const isSameUser = this.#checkUser(message.from?.entityId ?? '')
		let showAvatar = true

		if (isSameUser && this.lastDay) {
			showAvatar = this.#checkInterval(asDay)
		}

		this.lastDay = asDay

		return {
			id: message.message_id,
			showAvatar
		}
	}

	#checkInterval(day: Dayjs) {
		const minutes = day.diff(this.lastDay, 'minutes')
		return minutes > 1
	}

	#checkUser(user: string) {
		const isSame = this.#lastUser === user
		this.#lastUser = user

		return isSame
	}
}

function createMessageMap(messages: Message[]) {
	messages.sort(sortOldFirst)
	const days: Dayjs[] = []

	const transformer = new MessageTransformer()

	const map = messages.reduce<Record<string, MessageWithFlag[]>>(
		(map, message) => {
			const result = transformer.transform(message)

			if (!transformer.lastDay) {
				return map
			}

			const date = transformer.lastDay.startOf('day')
			const key = date.toISOString()

			if (key in map) {
				map[key].push(result)
			} else {
				days.push(date)
				map[key] = [result]
			}

			return map
		},
		{}
	)

	return { days, map }
}

const getConversationUrl = (conversation: string) =>
	ROUTES[1].replace(':conversation', conversation)

export {
	createMessageMap,
	findRenderContent,
	formatChatDate,
	formatDateTime,
	formatTime,
	getConversationUrl,
	sortNewFirst,
	sortOldFirst
}
